import { useEffect, useState } from 'react';
import { ResourceType, type ToolListItemResponse } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';

type TargetResource = { type: ResourceType; id: string };

export type ShareToolsWarningState =
  | { status: 'idle' }
  | { status: 'pending' }
  | {
      status: 'ready';
      hasConfiguredCredentials: boolean;
      affectedResourceCount: number;
      affectedToolCount: number;
      loadFailed: boolean;
    };

const NO_AUTH_TOOL_STRATEGIES = ['platform_managed', 'none'] as const;
const NO_AUTH_TOOL_STRATEGY_SET = new Set<string>(NO_AUTH_TOOL_STRATEGIES);
const FOLDER_ITEMS_PAGE_SIZE = 100;

function toolRequiresConfiguredCredentials(
  tool: ToolListItemResponse | undefined
): boolean {
  if (tool === undefined) {
    return false;
  }
  const nonLegacyStrategies = tool.credentialStrategyDescriptors.filter(
    (strategy) => strategy.name !== 'tool_by_name'
  );
  if (nonLegacyStrategies.length === 0) {
    return false;
  }
  return nonLegacyStrategies.some(
    (strategy) => !NO_AUTH_TOOL_STRATEGY_SET.has(strategy.name)
  );
}

/**
 * Whether anything being shared runs tools on credentials someone configured —
 * a chat's loaded tools, an agent's mentioned tools, and both inside shared
 * folders. People given access can trigger those integrations, so the share
 * dialog says so before access is handed out.
 */
export function useShareToolsWarning({
  client,
  resolvedTargets,
}: {
  client: Pick<TenantedClient, 'resource' | 'toolsV2'>;
  resolvedTargets: TargetResource[];
}): ShareToolsWarningState {
  const [toolsWarning, setToolsWarning] = useState<ShareToolsWarningState>({
    status: 'idle',
  });

  useEffect(() => {
    let cancelled = false;
    const setToolsWarningIfActive = (next: ShareToolsWarningState): void => {
      if (!cancelled) {
        setToolsWarning(next);
      }
    };

    const loadToolsWarning = async () => {
      const directChatOrAgentTargets = resolvedTargets.filter(
        (target) =>
          target.type === ResourceType.Chat ||
          target.type === ResourceType.Agent
      );
      const folderTargets = resolvedTargets.filter(
        (target) => target.type === ResourceType.Folder
      );

      const expandedTargetByKey = new Map<string, TargetResource>();
      for (const target of directChatOrAgentTargets) {
        expandedTargetByKey.set(`${target.type}:${target.id}`, target);
      }

      let loadFailed = false;

      for (const folderTarget of folderTargets) {
        const descendantResult =
          await client.resource.folder.getDescendantFolderIds({
            folderId: folderTarget.id,
          });
        if (descendantResult.isErr()) {
          loadFailed = true;
          continue;
        }

        const folderIds = [
          folderTarget.id,
          ...descendantResult.value.folderIds,
        ];
        for (const folderId of folderIds) {
          let offset = 0;
          //eslint-disable-next-line no-constant-condition
          while (true) {
            const folderItemsResult =
              await client.resource.folder.getFolderItems({
                folderId,
                itemTypes: [ResourceType.Chat, ResourceType.Agent],
                limit: FOLDER_ITEMS_PAGE_SIZE,
                offset,
                sort: 'position',
                sortDir: 'asc',
              });
            if (folderItemsResult.isErr()) {
              loadFailed = true;
              break;
            }

            for (const item of folderItemsResult.value.items) {
              if (item.type === ResourceType.Chat) {
                expandedTargetByKey.set(
                  `${ResourceType.Chat}:${item.data.id}`,
                  {
                    type: ResourceType.Chat,
                    id: item.data.id,
                  }
                );
                continue;
              }
              if (item.type === ResourceType.Agent) {
                expandedTargetByKey.set(
                  `${ResourceType.Agent}:${item.data.id}`,
                  {
                    type: ResourceType.Agent,
                    id: item.data.id,
                  }
                );
              }
            }

            offset += folderItemsResult.value.items.length;
            if (
              folderItemsResult.value.items.length === 0 ||
              offset >= folderItemsResult.value.total
            ) {
              break;
            }
          }
        }
      }

      const chatOrAgentTargets = [...expandedTargetByKey.values()];
      if (chatOrAgentTargets.length === 0) {
        setToolsWarningIfActive({
          status: 'ready',
          hasConfiguredCredentials: false,
          affectedResourceCount: 0,
          affectedToolCount: 0,
          loadFailed,
        });
        return;
      }

      setToolsWarningIfActive({ status: 'pending' });

      const toolsResult = await client.toolsV2.list({});
      if (toolsResult.isErr()) {
        setToolsWarningIfActive({
          status: 'ready',
          hasConfiguredCredentials: false,
          affectedResourceCount: 0,
          affectedToolCount: 0,
          loadFailed: true,
        });
        return;
      }

      const toolByKey = new Map<string, ToolListItemResponse>();
      for (const tool of toolsResult.value) {
        toolByKey.set(tool.key, tool);
      }

      const configuredToolKeys = new Set<string>();
      const affectedResourceIds = new Set<string>();

      for (const target of chatOrAgentTargets) {
        if (target.type === ResourceType.Chat) {
          const snapshotResult =
            await client.resource.chat.getConversationSnapshot({
              conversationId: target.id,
            });
          if (snapshotResult.isErr()) {
            loadFailed = true;
            continue;
          }
          const loadedToolIds =
            snapshotResult.value.conversation.chat.metadata?.loadedToolIds ??
            [];
          const hasConfiguredCredentials = loadedToolIds.some((toolKey) =>
            toolRequiresConfiguredCredentials(toolByKey.get(toolKey))
          );
          if (hasConfiguredCredentials) {
            affectedResourceIds.add(target.id);
            for (const toolKey of loadedToolIds) {
              if (toolRequiresConfiguredCredentials(toolByKey.get(toolKey))) {
                configuredToolKeys.add(toolKey);
              }
            }
          }
          continue;
        }

        const mentionedToolsResult =
          await client.resource.agent.getMentionedToolsStatus({
            id: target.id,
          });
        if (mentionedToolsResult.isErr()) {
          loadFailed = true;
          continue;
        }
        const configuredMentionedTools =
          mentionedToolsResult.value.tools.filter(
            (tool) =>
              tool.canExecute &&
              toolRequiresConfiguredCredentials(toolByKey.get(tool.key))
          );
        if (configuredMentionedTools.length > 0) {
          affectedResourceIds.add(target.id);
          for (const tool of configuredMentionedTools) {
            configuredToolKeys.add(tool.key);
          }
        }
      }

      setToolsWarningIfActive({
        status: 'ready',
        hasConfiguredCredentials: affectedResourceIds.size > 0,
        affectedResourceCount: affectedResourceIds.size,
        affectedToolCount: configuredToolKeys.size,
        loadFailed,
      });
    };

    void loadToolsWarning();

    return () => {
      cancelled = true;
    };
  }, [
    client.resource.agent,
    client.resource.chat,
    client.resource.folder,
    client.toolsV2,
    resolvedTargets,
  ]);

  return toolsWarning;
}
